/**
 * Flat, single-color nav marks derived from the full-art product lockups.
 *
 * The source artwork is raster, beveled and carries baked-in descriptor text,
 * which turns to mud below ~48px. These are stroke-only reductions of the same
 * symbols: one hexagon frame, one inner glyph, `currentColor` throughout, so a
 * mark inherits type color and works on light or dark ground.
 *
 * Full art still belongs on product pages; these belong in nav, cards and
 * anywhere the mark renders small.
 */

export type MarkProps = {
  size?: number;
  className?: string;
};

/** Pointy-top hexagon frame shared by every mark, matching the artwork. */
const HEX = "M16 1.5 29 9v14l-13 7.5L3 23V9z";

function Mark({
  size = 32,
  className,
  title,
  children,
}: MarkProps & { title: string; children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label={title}
      className={className}
    >
      <path d={HEX} opacity={0.55} />
      {children}
    </svg>
  );
}

/** Cerberus — three heads, reduced to three angular muzzles. */
export function CerberusMark(props: MarkProps) {
  return (
    <Mark {...props} title="Cerberus">
      <path d="M16 9.5l3.2 4.2v5.6L16 22.5l-3.2-3.2v-5.6z" />
      <path d="M11.4 12.2L9 15v4l2.6 2.2" opacity={0.75} />
      <path d="M20.6 12.2L23 15v4l-2.6 2.2" opacity={0.75} />
    </Mark>
  );
}

/** Argus — the all-seeing eye over a crosshair. */
export function ArgusMark(props: MarkProps) {
  return (
    <Mark {...props} title="Argus">
      <path d="M7.5 16c2.6-3.6 5.4-5.4 8.5-5.4s5.9 1.8 8.5 5.4c-2.6 3.6-5.4 5.4-8.5 5.4S10.1 19.6 7.5 16z" />
      <circle cx={16} cy={16} r={2.4} />
      <path d="M16 8.4v2.2M16 21.4v2.2" opacity={0.7} />
    </Mark>
  );
}

/** Mimir — decoy memory: an M above a ripple. */
export function MimirMark(props: MarkProps) {
  return (
    <Mark {...props} title="Mimir">
      <path d="M10 19v-8l6 5 6-5v8" />
      <path d="M12.5 22.5c1.4-1.2 5.6-1.2 7 0" opacity={0.7} />
    </Mark>
  );
}

/** Sentience — an S threaded through nodes. */
export function SentienceMark(props: MarkProps) {
  return (
    <Mark {...props} title="Sentience">
      <path d="M20.5 11.5c-1.4-1.1-3.1-1.5-4.8-1.2-2.4.4-3.4 2.6-1.9 4.1 1.2 1.2 4.4 1 5.6 2.2 1.5 1.5.5 3.7-1.9 4.1-1.7.3-3.4-.1-4.8-1.2" />
      <circle cx={11.5} cy={12} r={1.1} opacity={0.75} />
      <circle cx={20.5} cy={20} r={1.1} opacity={0.75} />
    </Mark>
  );
}

/** Bifrost — the B, with the bridge arcing out of its bowl. */
export function BifrostMark(props: MarkProps) {
  return (
    <Mark {...props} title="Bifrost">
      <path d="M12 21.5v-11h4.6c2 0 3.3 1.1 3.3 2.7s-1.3 2.6-3.3 2.6H12" />
      <path d="M16.6 15.8c2.4 0 3.9 1.2 3.9 2.9s-1.5 2.8-3.9 2.8" />
      <path d="M9.5 23.5c3-3.3 6.6-5.2 11-5.6" opacity={0.75} />
    </Mark>
  );
}

/** Guardian — a human authority point inside a protective boundary. */
export function GuardianMark(props: MarkProps) {
  return (
    <Mark {...props} title="Guardian">
      <path d="M16 9.2a3.1 3.1 0 1 1 0 6.2 3.1 3.1 0 0 1 0-6.2z" />
      <path d="M10.4 22.2c.9-3.1 2.8-4.8 5.6-4.8s4.7 1.7 5.6 4.8" />
      <path d="M8.6 10.4c1.3-2 3.8-3.4 7.4-3.4s6.1 1.4 7.4 3.4v5.9c0 4.1-2.5 7-7.4 8.7-4.9-1.7-7.4-4.6-7.4-8.7z" opacity={0.72} />
    </Mark>
  );
}

/** TraceLock — the TL monogram. */
export function TraceLockMark(props: MarkProps) {
  return (
    <Mark {...props} title="TraceLock">
      <path d="M9.5 10.5h8M13.5 10.5v11" />
      <path d="M18.5 10.5v11h5" />
    </Mark>
  );
}

/**
 * Warden — a portcullis: the gate of the stronghold.
 * New mark; no source artwork exists yet. Reads as a boundary that lets
 * things through under control, which is the services thesis.
 */
export function WardenMark(props: MarkProps) {
  return (
    <Mark {...props} title="Warden">
      <path d="M10.5 10.5h11" />
      <path d="M12.4 10.5v11M16 10.5v11M19.6 10.5v11" />
      <path d="M10.5 14.6h11M10.5 18.2h11" opacity={0.7} />
    </Mark>
  );
}

export const PRODUCT_MARKS = {
  cerberus: CerberusMark,
  argus: ArgusMark,
  mimir: MimirMark,
  sentience: SentienceMark,
  bifrost: BifrostMark,
  guardian: GuardianMark,
  tracelock: TraceLockMark,
  warden: WardenMark,
} as const;

export type ProductMarkKey = keyof typeof PRODUCT_MARKS;
